import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  MessageCircle,
  X,
  Send,
  HelpCircle,
  AlertTriangle,
  BookOpenText,
  Volume2,
  VolumeX,
  Maximize2,
  Minimize2,
  CheckCircle2,
} from 'lucide-react';
import { cn } from '../../lib/cn';
import { usePageSeoContext } from '../../lib/PageSeoContext';
import { fetchFaqItems, type FaqItem } from '../../lib/faqApi';
import { searchFaqItems, looksMultiIntent, MIN_FAQ_CONFIDENT_SCORE, type FaqMatch } from '../../lib/faqSearch';
import { logUnansweredQuestion } from '../../lib/unansweredQuestionsApi';
import {
  fetchCsrfToken,
  fetchCalendar,
  checkAvailability,
  submitBooking,
  toDateString,
  formatDate,
  type CalendarData,
  type AvailabilityResult,
  type DateRange,
} from '../../lib/bookingApi';
import NumberInput from '../ui/NumberInput';
import {
  WHATSAPP_BUTTON_ENABLED_KEY,
  TRUST_BADGE_ENABLED_KEY,
  TRUST_BADGE_SCRIPT_KEY,
} from '../../lib/siteSettingsApi';

const STARTER_PROMPTS = [
  'What time is check-in and check-out?',
  'Is parking available?',
  'Where is Homeland Stay located?',
  'Do the rooms have AC?',
  'What is the cancellation policy?',
];

const SOUND_PREF_KEY = 'homeland_chat_sound';

type BookingFields = { name: string; phone: string; email: string; stateCity: string; message: string };

interface ChatMessage {
  id: number;
  sender: 'user' | 'bot';
  text?: string;
  // The best FAQ match, plus (when the question looked like it was asking
  // about two things at once -- see looksMultiIntent) a second, genuinely
  // different-topic match to show alongside it.
  matches?: FaqMatch[];
  kind?:
    | 'pending'
    // FAQ outcomes. 'no-match': the FAQ list loaded fine and was actually
    // searched, just found nothing confident. 'error': the FAQ list itself
    // failed to load, so the question was never actually searched at all --
    // deserves its own wording rather than looking identical to 'no-match'.
    | 'no-match'
    | 'error'
    // The guided in-chat booking flow -- each step is its own bot message;
    // once answered it's "locked" into a *-done summary and a new message
    // asks the next question, so the thread reads like an actual
    // conversation rather than one form mutating in place.
    | 'booking-dates'
    | 'booking-dates-done'
    | 'booking-unavailable'
    | 'booking-guests'
    | 'booking-guests-done'
    | 'booking-details'
    | 'booking-details-done'
    | 'booking-success'
    | 'booking-failed';
  summary?: string;
  availability?: AvailabilityResult;
  bookingRef?: string;
}

// --- A short two-note chime, synthesized rather than an audio file (no
// asset to host/load) -- one shared AudioContext, created lazily on first
// use since browsers refuse to start one before a user gesture. ---
let audioCtx: AudioContext | null = null;
function playChime() {
  try {
    const Ctx = window.AudioContext || (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
    if (!Ctx) return;
    if (!audioCtx) audioCtx = new Ctx();
    if (audioCtx.state === 'suspended') audioCtx.resume();
    const osc = audioCtx.createOscillator();
    const gain = audioCtx.createGain();
    osc.connect(gain);
    gain.connect(audioCtx.destination);
    const now = audioCtx.currentTime;
    osc.frequency.setValueAtTime(587.33, now);
    osc.frequency.setValueAtTime(880, now + 0.08);
    gain.gain.setValueAtTime(0.09, now);
    gain.gain.linearRampToValueAtTime(0.0001, now + 0.25);
    osc.start(now);
    osc.stop(now + 0.26);
  } catch {
    // Autoplay/audio policy — never let a chime failure affect the chat.
  }
}

/** Free, local search-based assistant: FAQ answers come from the
 * admin-managed FAQ database (api/faq.php, edited at /admin/faq) — the same
 * one guests see on the /faq page. Booking requests go through the exact
 * same backend as the main booking form (fetchCalendar/checkAvailability/
 * submitBooking) — this is a second front end for that pipeline, not a
 * separate one. */
export default function ChatWidget() {
  const { siteSettings } = usePageSeoContext();
  const [open, setOpen] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [soundEnabled, setSoundEnabled] = useState(() => {
    try {
      return localStorage.getItem(SOUND_PREF_KEY) !== '0';
    } catch {
      return true;
    }
  });
  const [input, setInput] = useState('');
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const bodyRef = useRef<HTMLDivElement>(null);
  const nextId = useRef(1);

  // FAQ list: fetched once, the first time the panel opens. A ref (not
  // state) because nothing here needs it to trigger a render — callers
  // either already have a render-driven reason to re-run, or are inside an
  // async callback where a ref is the only thing that won't be stale.
  const faqItemsRef = useRef<FaqItem[] | null>(null);
  const faqErrorRef = useRef(false);
  const faqWaiters = useRef<(() => void)[]>([]);

  useEffect(() => {
    if (!open || faqItemsRef.current || faqErrorRef.current) return;
    fetchFaqItems()
      .then((items) => {
        faqItemsRef.current = items;
        faqWaiters.current.forEach((w) => w());
        faqWaiters.current = [];
      })
      .catch(() => {
        faqErrorRef.current = true;
        faqWaiters.current.forEach((w) => w());
        faqWaiters.current = [];
      });
  }, [open]);

  const waitForFaq = (): Promise<void> => {
    if (faqItemsRef.current || faqErrorRef.current) return Promise.resolve();
    return new Promise((resolve) => faqWaiters.current.push(resolve));
  };

  // Booking pipeline state — calendar/CSRF are fetched once, lazily, the
  // first time a guest actually starts the booking flow (not on open, most
  // visitors asking a question never need it).
  const calendarRef = useRef<CalendarData | null>(null);
  const csrfRef = useRef('');
  const bookingDraft = useRef<{ checkIn: string; checkOut: string; adults: number; kids: number } & BookingFields>({
    checkIn: '',
    checkOut: '',
    adults: 1,
    kids: 0,
    name: '',
    phone: '',
    email: '',
    stateCity: '',
    message: '',
  });

  const ensureCalendarAndCsrf = async () => {
    const tasks: Promise<void>[] = [];
    if (!calendarRef.current) {
      tasks.push(
        fetchCalendar()
          .then((d) => {
            calendarRef.current = d;
          })
          .catch(() => {}),
      );
    }
    if (!csrfRef.current) {
      tasks.push(
        fetchCsrfToken()
          .then((t) => {
            csrfRef.current = t;
          })
          .catch(() => {}),
      );
    }
    await Promise.all(tasks);
  };

  const number = siteSettings.social_whatsapp;
  const digits = number?.replace(/[^\d]/g, '') ?? '';
  const whatsappEnabled = siteSettings[WHATSAPP_BUTTON_ENABLED_KEY] !== '0' && !!digits;
  const trustBadgeActive = siteSettings[TRUST_BADGE_ENABLED_KEY] !== '0' && !!siteSettings[TRUST_BADGE_SCRIPT_KEY];
  const stackedBelow = [trustBadgeActive, whatsappEnabled].filter(Boolean).length;
  // Tighter gap above the WhatsApp button specifically than the default
  // Tailwind step gave (was a visually loose ~20-32px) — arbitrary values
  // so the gap is exactly 8px above WhatsApp's own 56px button regardless
  // of breakpoint, not just whichever spacing scale step happened to be
  // closest.
  const bottomClass =
    stackedBelow === 2 ? 'bottom-[164px] md:bottom-[168px]' : stackedBelow === 1 ? 'bottom-[84px] md:bottom-[88px]' : 'bottom-5 md:bottom-6';

  useEffect(() => {
    bodyRef.current?.scrollTo({ top: bodyRef.current.scrollHeight, behavior: 'smooth' });
  }, [messages]);

  const toggleSound = () => {
    setSoundEnabled((v) => {
      const next = !v;
      try {
        localStorage.setItem(SOUND_PREF_KEY, next ? '1' : '0');
      } catch {
        // Private browsing / storage disabled — the toggle still works for
        // this session, it just won't be remembered next visit.
      }
      return next;
    });
  };

  /** Every bot message appears after a brief randomized "thinking" delay
   * with a typing indicator, then a short chime -- applies uniformly to an
   * FAQ answer and to each step of the booking flow, so the assistant feels
   * consistent throughout rather than instant for one kind of reply and
   * delayed for another. `build` runs its real work (a search, an
   * availability check, a network submission) concurrently with the
   * minimum delay, not after it, so a slow network call never adds its own
   * latency on top of the simulated one. */
  const sendBotMessageAsync = (build: () => Promise<Omit<ChatMessage, 'id' | 'sender'>> | Omit<ChatMessage, 'id' | 'sender'>) => {
    const thinkingId = nextId.current++;
    setMessages((m) => [...m, { id: thinkingId, sender: 'bot', kind: 'pending' }]);
    const minDelay = new Promise<void>((resolve) => setTimeout(resolve, 2000 + Math.random() * 3000));
    Promise.all([Promise.resolve(build()), minDelay]).then(([content]) => {
      setMessages((m) => m.map((msg) => (msg.id === thinkingId ? { ...msg, ...content } : msg)));
      if (soundEnabled) playChime();
    });
  };

  const lockMessage = (id: number, patch: Partial<ChatMessage>) => {
    setMessages((m) => m.map((msg) => (msg.id === id ? { ...msg, ...patch } : msg)));
  };

  const whatsappHref = (question: string) =>
    `https://wa.me/${digits}?text=${encodeURIComponent(`Namaste Homeland Stay, I have a question from your website: ${question}`)}`;

  // --- FAQ search ---

  const answerFrom = (items: FaqItem[], question: string): FaqMatch[] => {
    const found = searchFaqItems(items, question, 2);
    if (found.length === 0 || found[0].score < MIN_FAQ_CONFIDENT_SCORE) return [];
    if (
      found.length > 1 &&
      looksMultiIntent(question) &&
      found[1].score >= MIN_FAQ_CONFIDENT_SCORE &&
      found[1].item.category !== found[0].item.category
    ) {
      return found;
    }
    return [found[0]];
  };

  const ask = (question: string) => {
    const text = question.trim();
    if (!text) return;
    setInput('');
    setMessages((m) => [...m, { id: nextId.current++, sender: 'user', text }]);
    sendBotMessageAsync(async () => {
      await waitForFaq();
      if (faqErrorRef.current || !faqItemsRef.current) return { kind: 'error' as const, text };
      const matches = answerFrom(faqItemsRef.current, text);
      if (matches.length === 0) {
        logUnansweredQuestion(text);
        return { kind: 'no-match' as const, text };
      }
      // kind: undefined explicitly clears the 'pending' it's replacing --
      // the merge below is a shallow spread, so a result object that
      // simply omits `kind` would otherwise leave the old 'pending' value
      // in place forever and this answer would never stop showing as the
      // typing indicator despite `matches` being set correctly underneath.
      return { kind: undefined, matches };
    });
  };

  // --- Guided booking flow ---

  const startBooking = () => {
    setMessages((m) => [...m, { id: nextId.current++, sender: 'user', text: "I'd like to book a stay" }]);
    sendBotMessageAsync(async () => {
      await ensureCalendarAndCsrf();
      return { kind: 'booking-dates' as const };
    });
  };

  const submitBookingDates = (formId: number, checkIn: string, checkOut: string) => {
    bookingDraft.current = { ...bookingDraft.current, checkIn, checkOut };
    lockMessage(formId, {
      kind: 'booking-dates-done',
      summary: `📅 ${formatDate(checkIn)} → ${formatDate(checkOut)}`,
      availability: undefined,
    });
    sendBotMessageAsync(async () => {
      await ensureCalendarAndCsrf();
      const data = calendarRef.current;
      const today = data?.serverToday ?? toDateString(new Date());
      const result = checkAvailability(checkIn, checkOut, data?.bookings ?? [], today);
      if (!result.available) return { kind: 'booking-unavailable' as const, availability: result };
      return { kind: 'booking-guests' as const };
    });
  };

  const retryBookingDates = (formId: number) => {
    lockMessage(formId, { kind: 'booking-dates', availability: undefined });
  };

  const submitBookingGuests = (formId: number, adults: number, kids: number) => {
    bookingDraft.current = { ...bookingDraft.current, adults, kids };
    const guestLabel = `${adults} adult${adults === 1 ? '' : 's'}${kids > 0 ? ` + ${kids} kid${kids === 1 ? '' : 's'}` : ''}`;
    lockMessage(formId, { kind: 'booking-guests-done', summary: guestLabel });
    sendBotMessageAsync(() => ({ kind: 'booking-details' as const }));
  };

  const submitBookingDetails = (formId: number, fields: BookingFields) => {
    bookingDraft.current = { ...bookingDraft.current, ...fields };
    lockMessage(formId, { kind: 'booking-details-done', summary: `${fields.name} · ${fields.phone}` });
    const draft = bookingDraft.current;
    sendBotMessageAsync(async () => {
      await ensureCalendarAndCsrf();
      try {
        const res = await submitBooking(
          {
            name: draft.name,
            phone: draft.phone,
            email: draft.email,
            stateCity: draft.stateCity,
            checkIn: draft.checkIn,
            checkOut: draft.checkOut,
            adults: draft.adults,
            kids: draft.kids,
            message: draft.message,
            docNumbers: {},
            docFiles: {},
          },
          csrfRef.current,
        );
        if (res.success) {
          return { kind: 'booking-success' as const, text: res.message, bookingRef: res.booking_reference };
        }
        return { kind: 'booking-failed' as const, text: res.message ?? 'An error occurred. Please try again.' };
      } catch {
        return { kind: 'booking-failed' as const, text: 'Connection error. Please check your internet and try again.' };
      }
    });
  };

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-label={open ? 'Close chat' : 'Ask a question about Homeland Stay'}
        className={cn(
          'fixed right-5 md:right-6 z-50 w-14 h-14 rounded-full bg-olive flex items-center justify-center text-paper shadow-[0_6px_20px_rgba(0,0,0,0.28)] transition-transform duration-200 hover:scale-105 active:scale-95 border-2 border-gold',
          bottomClass,
        )}
      >
        {open ? <X size={24} /> : <MessageCircle size={26} />}
      </button>

      {open && (
        <div
          className={cn(
            'fixed z-50 flex flex-col',
            expanded
              ? 'inset-0 bg-ink/50 items-center justify-center p-4 md:p-8'
              : cn(
                  'right-4 md:right-6',
                  stackedBelow === 2
                    ? 'bottom-[236px] md:bottom-[240px]'
                    : stackedBelow === 1
                      ? 'bottom-[156px] md:bottom-[160px]'
                      : 'bottom-[92px] md:bottom-[96px]',
                ),
          )}
          onClick={
            expanded
              ? (e) => {
                  if (e.target === e.currentTarget) setExpanded(false);
                }
              : undefined
          }
        >
          <div
            className={cn(
              'bg-paper rounded-2xl shadow-[0_16px_40px_rgba(0,0,0,0.22)] border border-gold/40 flex flex-col overflow-hidden',
              expanded ? 'w-full max-w-[640px] h-[85vh] max-h-[800px]' : 'w-[360px] max-w-[calc(100vw-2rem)] h-[520px] max-h-[calc(100vh-140px)]',
            )}
          >
            <div className="bg-olive px-4 py-3.5 border-b-2 border-gold shrink-0 flex items-center justify-between gap-3">
              <div className="flex items-center gap-2.5 min-w-0">
                <div className="relative shrink-0">
                  <div className="w-10 h-10 rounded-full overflow-hidden border border-white/25 bg-paper">
                    <img src="/chat-logo.png" alt="" className="w-full h-full object-cover" />
                  </div>
                  <span className="absolute -bottom-0.5 -right-0.5 w-2.5 h-2.5 bg-green-400 border-2 border-olive rounded-full" aria-hidden />
                </div>
                <div className="min-w-0">
                  <h3 className="font-serif font-semibold text-[14px] text-paper m-0 truncate">Homeland Stay Assistant</h3>
                  <p className="text-[10.5px] text-gold m-0 mt-0.5">Online</p>
                </div>
              </div>
              <div className="flex items-center gap-1 shrink-0">
                <button
                  type="button"
                  onClick={toggleSound}
                  aria-label={soundEnabled ? 'Mute chat sound' : 'Unmute chat sound'}
                  title={soundEnabled ? 'Mute' : 'Unmute'}
                  className="w-7 h-7 flex items-center justify-center rounded-full bg-transparent text-paper/80 border-none cursor-pointer hover:bg-paper/15 hover:text-paper transition-colors"
                >
                  {soundEnabled ? <Volume2 size={14} /> : <VolumeX size={14} />}
                </button>
                <button
                  type="button"
                  onClick={() => setExpanded((v) => !v)}
                  aria-label={expanded ? 'Collapse chat' : 'Expand chat'}
                  title={expanded ? 'Collapse' : 'Expand'}
                  className="w-7 h-7 flex items-center justify-center rounded-full bg-transparent text-paper/80 border-none cursor-pointer hover:bg-paper/15 hover:text-paper transition-colors"
                >
                  {expanded ? <Minimize2 size={14} /> : <Maximize2 size={14} />}
                </button>
                <button
                  type="button"
                  onClick={() => setOpen(false)}
                  aria-label="Close chat"
                  className="w-7 h-7 flex items-center justify-center rounded-full bg-transparent text-paper/80 border-none cursor-pointer hover:bg-paper/15 hover:text-paper transition-colors"
                >
                  <X size={16} />
                </button>
              </div>
            </div>

            <div ref={bodyRef} className="flex-1 overflow-y-auto p-3.5 flex flex-col gap-3 bg-sand/40">
              <BotBubble>
                Namaste! 🙏 Ask me anything about Homeland Stay, or I can help you book your stay right here.
                <div className="flex flex-wrap gap-1.5 mt-2.5">
                  <button
                    type="button"
                    onClick={startBooking}
                    className="px-2.5 py-1.5 rounded-full bg-olive text-paper text-[11px] font-semibold border-none cursor-pointer hover:bg-olive-dark transition-colors"
                  >
                    Book a Stay
                  </button>
                  {STARTER_PROMPTS.map((q) => (
                    <button
                      key={q}
                      type="button"
                      onClick={() => ask(q)}
                      className="px-2.5 py-1.5 rounded-full border border-gold text-[11px] font-semibold text-olive-dark bg-paper cursor-pointer hover:bg-olive hover:text-paper transition-colors"
                    >
                      {q}
                    </button>
                  ))}
                </div>
              </BotBubble>

              {messages.map((m) => {
                if (m.sender === 'user') return <UserBubble key={m.id}>{m.text}</UserBubble>;

                switch (m.kind) {
                  case 'pending':
                    return (
                      <BotBubble key={m.id}>
                        <TypingDots />
                      </BotBubble>
                    );
                  case 'error':
                    return (
                      <BotBubble key={m.id} tone="error">
                        <div className="flex items-start gap-1.5">
                          <AlertTriangle size={14} className="text-danger shrink-0 mt-0.5" />
                          <span>Our search is having trouble loading right now — not that there's no answer, just that I couldn't check. Our host can help directly:</span>
                        </div>
                        <AskHostActions question={m.text ?? ''} whatsappEnabled={whatsappEnabled} whatsappHref={whatsappHref} onBook={startBooking} />
                      </BotBubble>
                    );
                  case 'no-match':
                    return (
                      <BotBubble key={m.id} tone="warn">
                        <div className="flex items-start gap-1.5">
                          <HelpCircle size={14} className="text-gold-press shrink-0 mt-0.5" />
                          <span>I couldn't find a confident answer to that on our site. Our host can help directly:</span>
                        </div>
                        <AskHostActions question={m.text ?? ''} whatsappEnabled={whatsappEnabled} whatsappHref={whatsappHref} onBook={startBooking} />
                      </BotBubble>
                    );
                  case 'booking-dates':
                    return (
                      <BotBubble key={m.id}>
                        <BookingDatesForm onSubmit={(ci, co) => submitBookingDates(m.id, ci, co)} />
                      </BotBubble>
                    );
                  case 'booking-dates-done':
                  case 'booking-guests-done':
                  case 'booking-details-done':
                    return (
                      <BotBubble key={m.id} tone="locked">
                        {m.summary}
                      </BotBubble>
                    );
                  case 'booking-unavailable':
                    return (
                      <BotBubble key={m.id} tone="warn">
                        <BookingUnavailable
                          availability={m.availability!}
                          onUseDates={(r) => submitBookingDates(m.id, r.checkIn, r.checkOut)}
                          onRetry={() => retryBookingDates(m.id)}
                        />
                      </BotBubble>
                    );
                  case 'booking-guests':
                    return (
                      <BotBubble key={m.id}>
                        <BookingGuestsForm onSubmit={(a, k) => submitBookingGuests(m.id, a, k)} />
                      </BotBubble>
                    );
                  case 'booking-details':
                    return (
                      <BotBubble key={m.id}>
                        <BookingDetailsForm onSubmit={(fields) => submitBookingDetails(m.id, fields)} />
                      </BotBubble>
                    );
                  case 'booking-success':
                    return (
                      <BotBubble key={m.id}>
                        <div className="flex items-start gap-1.5">
                          <CheckCircle2 size={14} className="text-olive shrink-0 mt-0.5" />
                          <div>
                            <p className="m-0 mb-1 font-semibold text-ink">{m.text ?? 'Your booking request has been sent!'}</p>
                            {m.bookingRef && (
                              <p className="m-0 text-[11.5px] text-muted">
                                Reference: <span className="font-mono font-semibold text-ink">{m.bookingRef}</span>
                              </p>
                            )}
                          </div>
                        </div>
                      </BotBubble>
                    );
                  case 'booking-failed':
                    return (
                      <BotBubble key={m.id} tone="error">
                        <div className="flex items-start gap-1.5">
                          <AlertTriangle size={14} className="text-danger shrink-0 mt-0.5" />
                          <span>{m.text}</span>
                        </div>
                        <AskHostActions question="my booking request" whatsappEnabled={whatsappEnabled} whatsappHref={whatsappHref} onBook={startBooking} />
                      </BotBubble>
                    );
                  default:
                    return (
                      <BotBubble key={m.id}>
                        {m.matches!.map((match, i) => (
                          <div key={match.item.id} className={i > 0 ? 'mt-3 pt-3 border-t border-hairline' : ''}>
                            {m.matches!.length > 1 && (
                              <p className="m-0 mb-1 text-[11px] font-semibold uppercase tracking-[0.04em] text-olive-dark">{match.item.question}</p>
                            )}
                            {match.item.answer.map((paragraph, pi) => (
                              <p key={pi} className={pi === match.item.answer.length - 1 && !match.item.note_text ? 'm-0' : 'mb-1.5'}>
                                {paragraph}
                              </p>
                            ))}
                            {match.item.note_text && <p className="m-0 text-[11.5px] text-olive-dark font-medium">{match.item.note_text}</p>}
                          </div>
                        ))}
                      </BotBubble>
                    );
                }
              })}
            </div>

            <form
              onSubmit={(e) => {
                e.preventDefault();
                ask(input);
              }}
              className="flex gap-2 p-2.5 border-t border-hairline bg-paper shrink-0 shadow-[0_-2px_8px_rgba(0,0,0,0.04)]"
            >
              <input
                value={input}
                onChange={(e) => setInput(e.target.value)}
                placeholder="Ask anything about the stay..."
                autoComplete="off"
                className="flex-1 px-3.5 py-2 rounded-full border border-hairline text-[12.5px] outline-none transition-colors focus:border-olive"
              />
              <button
                type="submit"
                aria-label="Send"
                className="w-9 h-9 shrink-0 rounded-full bg-olive text-paper flex items-center justify-center border-none cursor-pointer transition-transform hover:bg-olive-dark active:scale-90"
              >
                <Send size={15} />
              </button>
            </form>
          </div>
        </div>
      )}
    </>
  );
}

function BotBubble({ children, tone = 'neutral' }: { children: React.ReactNode; tone?: 'neutral' | 'warn' | 'error' | 'locked' }) {
  return (
    <div
      className={cn(
        'max-w-[88%] self-start bg-paper border border-hairline rounded-2xl rounded-bl-md px-3.5 py-2.5 text-[12.5px] leading-relaxed text-ink shadow-[0_1px_3px_rgba(33,32,28,0.05)]',
        tone === 'warn' && 'border-l-[3px] border-l-gold',
        tone === 'error' && 'border-l-[3px] border-l-danger',
        tone === 'locked' && 'opacity-70 text-muted bg-transparent shadow-none',
      )}
    >
      {children}
    </div>
  );
}

function UserBubble({ children }: { children: React.ReactNode }) {
  return (
    <div className="max-w-[84%] self-end bg-olive text-paper rounded-2xl rounded-br-md px-3.5 py-2.5 text-[12.5px] leading-relaxed shadow-[0_1px_3px_rgba(33,32,28,0.12)]">
      {children}
    </div>
  );
}

/** The "couldn't actually check" / "no confident match" bubbles both end
 * with the same ways to reach a real person or get a booking moving --
 * kept as one component so the two don't drift apart in copy or styling. */
function AskHostActions({
  question,
  whatsappEnabled,
  whatsappHref,
  onBook,
}: {
  question: string;
  whatsappEnabled: boolean;
  whatsappHref: (q: string) => string;
  onBook: () => void;
}) {
  return (
    <div className="flex flex-wrap gap-1.5 mt-2">
      {whatsappEnabled && (
        <a
          href={whatsappHref(question)}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-md bg-[#25D366] text-white text-[11.5px] font-semibold no-underline"
        >
          <MessageCircle size={12} /> Ask on WhatsApp
        </a>
      )}
      <button
        type="button"
        onClick={onBook}
        className="px-2.5 py-1.5 rounded-md bg-gold text-ink text-[11.5px] font-semibold border-none cursor-pointer"
      >
        Book a stay →
      </button>
      <Link
        to="/faq"
        className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-md border border-hairline text-[11.5px] font-semibold text-ink-soft no-underline"
      >
        <BookOpenText size={12} /> Browse FAQs
      </Link>
    </div>
  );
}

/** A lightweight "thinking" indicator for the randomized delay before every
 * bot reply -- without it, that gap would just look frozen. */
function TypingDots() {
  return (
    <div className="flex items-center gap-1 py-0.5" aria-label="Thinking…">
      <span className="w-[6px] h-[6px] rounded-full bg-olive/45 animate-bounce [animation-delay:-0.3s]" />
      <span className="w-[6px] h-[6px] rounded-full bg-olive/45 animate-bounce [animation-delay:-0.15s]" />
      <span className="w-[6px] h-[6px] rounded-full bg-olive/45 animate-bounce" />
    </div>
  );
}

const bookingFieldClass =
  'w-full px-2.5 py-2 rounded-md border border-hairline text-[12.5px] outline-none focus:border-olive bg-white';
const bookingLabelClass = 'block text-[10px] font-semibold uppercase tracking-[0.05em] text-muted mb-1';
const bookingButtonClass =
  'w-full py-2 rounded-md bg-olive text-paper text-[12.5px] font-semibold border-none cursor-pointer hover:bg-olive-dark transition-colors';

function BookingDatesForm({ onSubmit }: { onSubmit: (checkIn: string, checkOut: string) => void }) {
  const today = toDateString(new Date());
  const [checkIn, setCheckIn] = useState('');
  const [checkOut, setCheckOut] = useState('');
  const [error, setError] = useState('');

  const submit = () => {
    if (!checkIn || !checkOut) {
      setError('Please pick both dates.');
      return;
    }
    if (checkOut <= checkIn) {
      setError('Check-out must be after check-in.');
      return;
    }
    setError('');
    onSubmit(checkIn, checkOut);
  };

  return (
    <div>
      <p className="m-0 mb-2.5">Sure! What dates would you like to stay?</p>
      <div className="grid grid-cols-2 gap-2 mb-2">
        <div>
          <label className={bookingLabelClass}>Check-in</label>
          <input type="date" min={today} value={checkIn} onChange={(e) => setCheckIn(e.target.value)} className={bookingFieldClass} />
        </div>
        <div>
          <label className={bookingLabelClass}>Check-out</label>
          <input type="date" min={checkIn || today} value={checkOut} onChange={(e) => setCheckOut(e.target.value)} className={bookingFieldClass} />
        </div>
      </div>
      {error && <p className="m-0 mb-2 text-[11px] text-danger">{error}</p>}
      <button type="button" onClick={submit} className={bookingButtonClass}>
        Check Availability
      </button>
    </div>
  );
}

function BookingUnavailable({
  availability,
  onUseDates,
  onRetry,
}: {
  availability: AvailabilityResult;
  onUseDates: (range: DateRange) => void;
  onRetry: () => void;
}) {
  return (
    <div>
      <div className="flex items-start gap-1.5 mb-2">
        <AlertTriangle size={14} className="text-gold-press shrink-0 mt-0.5" />
        <span>{availability.reason ?? "Those dates aren't available."}</span>
      </div>
      {availability.nextAvailable && (
        <button
          type="button"
          onClick={() => onUseDates(availability.nextAvailable!)}
          className="block w-full text-left px-2.5 py-2 mb-1.5 rounded-md border border-olive/40 text-[12px] font-semibold text-olive-dark bg-sand/60 cursor-pointer hover:bg-sand"
        >
          Next available: {formatDate(availability.nextAvailable.checkIn)} → {formatDate(availability.nextAvailable.checkOut)} (
          {availability.nextAvailable.days} night{availability.nextAvailable.days === 1 ? '' : 's'})
        </button>
      )}
      {availability.alternatives?.map((alt, i) => (
        <button
          key={i}
          type="button"
          onClick={() => onUseDates(alt)}
          className="block w-full text-left px-2.5 py-2 mb-1.5 rounded-md border border-hairline text-[12px] font-semibold text-ink-soft bg-paper cursor-pointer hover:border-olive"
        >
          {formatDate(alt.checkIn)} → {formatDate(alt.checkOut)} ({alt.days} night{alt.days === 1 ? '' : 's'})
        </button>
      ))}
      <button
        type="button"
        onClick={onRetry}
        className="w-full mt-1 py-2 rounded-md border border-dashed border-hairline text-[12px] font-semibold text-muted cursor-pointer hover:border-olive hover:text-olive bg-transparent"
      >
        Try different dates
      </button>
    </div>
  );
}

function BookingGuestsForm({ onSubmit }: { onSubmit: (adults: number, kids: number) => void }) {
  const [adults, setAdults] = useState(1);
  const [kids, setKids] = useState(0);
  return (
    <div>
      <p className="m-0 mb-2.5">Great, it's available! How many guests?</p>
      <div className="grid grid-cols-2 gap-2 mb-2.5">
        <div>
          <label className={bookingLabelClass}>Adults</label>
          <NumberInput min={1} max={9} value={adults} onChange={setAdults} className={bookingFieldClass} />
        </div>
        <div>
          <label className={bookingLabelClass}>Kids (under 12)</label>
          <NumberInput min={0} value={kids} onChange={setKids} className={bookingFieldClass} />
        </div>
      </div>
      <button type="button" onClick={() => onSubmit(adults, kids)} className={bookingButtonClass}>
        Next
      </button>
    </div>
  );
}

function BookingDetailsForm({ onSubmit }: { onSubmit: (fields: BookingFields) => void }) {
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [email, setEmail] = useState('');
  const [stateCity, setStateCity] = useState('');
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');

  const submit = () => {
    if (!name.trim()) {
      setError('Please enter your name.');
      return;
    }
    if (!phone.trim()) {
      setError('Please enter your phone number.');
      return;
    }
    // Unlike the main booking form's own client-side check, the backend
    // (public_booking.php) actually rejects the whole request if email is
    // empty or not a valid address -- found by testing this exact flow.
    // Catching it here gives a clear, immediate message instead of a
    // generic "please fill in all required fields" after the real
    // submission attempt.
    if (!email.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())) {
      setError('Please enter a valid email address.');
      return;
    }
    setError('');
    onSubmit({ name, phone, email, stateCity, message });
  };

  return (
    <div>
      <p className="m-0 mb-2.5">Last step — your contact details:</p>
      <div className="flex flex-col gap-1.5 mb-2">
        <input type="text" placeholder="Your full name" value={name} onChange={(e) => setName(e.target.value)} className={bookingFieldClass} />
        <input type="tel" placeholder="Phone number" value={phone} onChange={(e) => setPhone(e.target.value)} className={bookingFieldClass} />
        <input type="email" placeholder="Email address" value={email} onChange={(e) => setEmail(e.target.value)} className={bookingFieldClass} />
        <input
          type="text"
          placeholder="State & city (optional)"
          value={stateCity}
          onChange={(e) => setStateCity(e.target.value)}
          className={bookingFieldClass}
        />
        <textarea
          placeholder="Anything else? (optional)"
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          rows={2}
          className={bookingFieldClass}
        />
      </div>
      {error && <p className="m-0 mb-2 text-[11px] text-danger">{error}</p>}
      <button type="button" onClick={submit} className={bookingButtonClass}>
        Send Booking Request
      </button>
    </div>
  );
}
